import { createElement } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ACCOUNT_WORKFLOW_STATUSES, AccountWorkflowStatus, AccountStatusCounts, Skeleton, withStyles } from 'vopay';
import { BodyText, Card } from 'UI';
import { useModuleFeatureFlag } from '@/providers/FeatureFlag.provider';
import usePartnerAccountStatusLabel from '@/components/Entities/hooks/usePartnerAccountStatusLabel';
import styles from './AccountsOverview.module.scss';

const cn = withStyles(styles);

/** Statuses an account holds once it has left (or been pulled out of) the onboarding pipeline. */
const TERMINAL_STATUSES: ReadonlySet<AccountWorkflowStatus> = new Set<AccountWorkflowStatus>([
  'Active',
  'Disabled',
  'Rejected',
  'Blocked',
  'Suspended',
]);

/** Everything else: the statuses an account passes through on its way to one of the above. */
const ONBOARDING_STATUSES = ACCOUNT_WORKFLOW_STATUSES.filter((status) => !TERMINAL_STATUSES.has(status));

/** Membership test for "is this a status this frontend knows about", over the whole vocabulary. */
const KNOWN_STATUSES: ReadonlySet<string> = new Set<string>(ACCOUNT_WORKFLOW_STATUSES);

/** The partner portal's onboarding page; the stage and account type travel as its filters. */
const ONBOARDING_PATH = '/onboarding-form-tracker';

type AccountStatusSummaryCardProps = {
  fetching: boolean;
  numTotalAccounts: number;
  /**
   * Partial while `fetching`; every documented status is present once the summary has responded.
   * The backend keys these off the live `VIOAccounts.AccountStatus` enum, so the response can also
   * carry a status newer than `AccountWorkflowStatus` — see `unrecognizedStages` below.
   */
  statusCounts: Partial<AccountStatusCounts>;
  /**
   * The stages this partner's accounts pass through, in workflow order (see `getOnboardingPipeline`).
   * Always rendered, zero included; any other populated onboarding status is appended after them.
   */
  pipelineStages: AccountWorkflowStatus[];
};

export default function AccountStatusSummaryCard({
  fetching,
  numTotalAccounts,
  statusCounts,
  pipelineStages,
}: AccountStatusSummaryCardProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AccountsOverview' });
  const history = useHistory();
  const { resolveStatusLabel } = usePartnerAccountStatusLabel();
  const { isEnabled: canOpenOnboarding } = useModuleFeatureFlag('onBoardingFormTracker');

  const numActive = statusCounts.Active ?? 0;

  // Widened only to read keys the union does not name. Everything derived from the vocabulary below
  // stays typed; this is the one place the raw response shape is acknowledged.
  const counts: Record<string, number | undefined> = statusCounts;

  /**
   * Statuses the response carried that this frontend has no name for yet — a status added to the
   * backend enum before `AccountWorkflowStatus` catches up. They are deliberately kept out of the
   * typed lists: `resolveStatusLabel` renders them from their raw value, and showing them is what
   * keeps the card's promise that a stage holding accounts is never hidden.
   */
  const unrecognizedStages: string[] = Object.keys(counts).filter(
    (status) => !KNOWN_STATUSES.has(status) && (counts[status] ?? 0) > 0,
  );

  const numOnboarding =
    ONBOARDING_STATUSES.reduce((sum, status) => sum + (statusCounts[status] ?? 0), 0) +
    unrecognizedStages.reduce((sum, status) => sum + (counts[status] ?? 0), 0);

  const stages: AccountWorkflowStatus[] = [
    ...pipelineStages,
    ...ONBOARDING_STATUSES.filter((status) => (statusCounts[status] ?? 0) > 0 && !pipelineStages.includes(status)),
  ];

  function openOnboardingStage(stage: string) {
    history.push({
      pathname: ONBOARDING_PATH,
      search: new URLSearchParams({ stage, accountType: 'Account' }).toString(),
    });
  }

  return (
    <Card variant="section" className={cn('card', 'summaryCard')} data-testid="accountStatusSummaryCard">
      <div className={cn('totalBlock')}>
        {fetching ? <Skeleton width={40} height={46} /> : <span className={cn('total')}>{numTotalAccounts}</span>}
        {/* Body/16/Regular, Text/Secondary75 */}
        <BodyText className={cn('secondaryText')}>{t('accounts', { defaultValue: 'Accounts' })}</BodyText>
      </div>

      <div className={cn('divider')} />

      <div className={cn('breakdown')}>
        {fetching ? (
          <Skeleton width={200} height={18} />
        ) : (
          <div className={cn('legend')}>
            {/* Body/13/Medium */}
            <BodyText size="sm" weight="md" className={cn('legendItem', 'legendActive')}>
              {t('active', { defaultValue: '{{count}} active', count: numActive })}
            </BodyText>

            <BodyText size="sm" weight="md" className={cn('legendItem', 'legendOnboarding')}>
              {t('inOnboarding', { defaultValue: '{{count}} in onboarding', count: numOnboarding })}
            </BodyText>
          </div>
        )}

        <div className={cn('tiles')}>
          {[...stages, ...unrecognizedStages].map((stage) => {
            const count = counts[stage] ?? 0;

            // A tile is a <button>, which can't hold BodyText's block <div>s, so its Body/13 text
            // styles live in the module SCSS (same exception the figma-to-vopay skill makes for
            // option-card buttons).
            const tileContent = (
              <>
                <span className={cn('tileLabel')}>{resolveStatusLabel(stage)}</span>
                {fetching ? <Skeleton width={12} height={18} /> : <span className={cn('tileCount')}>{count}</span>}
              </>
            );

            // One element either way; only a partner with tracker access gets a clickable tile.
            return createElement(
              canOpenOnboarding ? 'button' : 'div',
              {
                key: stage,
                className: cn('tile', count === 0 && 'tileEmpty'),
                'data-testid': 'onboardingStageTile',
                ...(canOpenOnboarding && { type: 'button', onClick: () => openOnboardingStage(stage) }),
              },
              tileContent,
            );
          })}
        </div>
      </div>
    </Card>
  );
}
